/* ------------------------------------------------------------------
   خروجی اکسل در مرورگر — نویسندهٔ کوچکِ xlsx بدون وابستگی
   ------------------------------------------------------------------
   🔴 چرا در مرورگر و نه با یک Endpointِ تازه:

   جدول‌های پروندهٔ پرسنلی (تردد، مرخصی، گزارش کار) داده‌شان همین حالا
   در صفحه است، با همان مجوزی که سرور برای دیدنش سنجیده. خروجی از
   **همان ردیف‌های نمایش‌داده‌شده** ساخته می‌شود؛ مسیرِ دومِ خواندن
   یعنی دو پاسخ که می‌توانند با هم نخوانند — فایلی که با صفحه فرق دارد.

   ⚠️ این خروجیِ «مالی» نیست. خروجیِ کارکرد برای حقوق همچنان از
   `MonthlyExportBuilder` و نسخهٔ دوره می‌آید (BR-020).

   ⚠️ چرا کتابخانه نه: SheetJS در npm نسخهٔ قدیمی و آسیب‌پذیر دارد و
   ExcelJS برای چند جدولِ ساده سنگین است. فرمتِ لازم اینجا کوچک است:
   رشته، عدد، و مدت‌زمان — در یک ZIPِ بدون فشرده‌سازی.
   ------------------------------------------------------------------ */

/**
 * مقدارِ یک خانه.
 *
 * ⚠️ مدت‌زمان با `{ minutes }` جدا شده و نه یک رشتهٔ «۲س ۱۵د»: رشته
 * در اکسل جمع‌زدنی نیست و خودِ دلیلِ گرفتنِ خروجی همین جمع‌زدن است.
 */
export type XlsxCell = string | number | null | undefined | { minutes: number }

export interface XlsxSheet {
  /** نام برگه — بیش از ۳۱ نویسه و نویسه‌های ممنوع خودکار حذف می‌شوند. */
  name: string
  header: string[]
  rows: XlsxCell[][]
  /** پهنای ستون‌ها بر حسب نویسه. */
  widths?: number[]
}

/* شمارهٔ سبک‌ها در `styles.xml` */
const STYLE_HEADER = 1
const STYLE_DURATION = 2

export function buildXlsx(sheets: XlsxSheet[]): Blob {
  const names = uniqueSheetNames(sheets.map((s) => s.name))

  const files: Array<[string, string]> = [
    ['[Content_Types].xml', contentTypes(sheets.length)],
    ['_rels/.rels', ROOT_RELS],
    ['xl/workbook.xml', workbook(names)],
    ['xl/_rels/workbook.xml.rels', workbookRels(sheets.length)],
    ['xl/styles.xml', STYLES],
    ...sheets.map((s, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, worksheet(s)]),
  ]

  return new Blob([zip(files)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

/**
 * ساخت و دانلودِ فایل.
 *
 * ⚠️ نویسه‌های ممنوعِ نام فایل در ویندوز حذف می‌شوند — نام کارمند
 * ممکن است «/» داشته باشد و مرورگر آن را بی‌صدا به «_» یا خالی بدل کند.
 */
export function downloadXlsx(fileName: string, sheets: XlsxSheet[]): void {
  const blob = buildXlsx(sheets)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = url
  a.download = `${fileName.replace(/[\\/:*?"<>|]+/g, '-').trim()}.xlsx`
  a.click()

  URL.revokeObjectURL(url)
}

/* ------------------------------------------------------------------
   بخش‌های SpreadsheetML
   ------------------------------------------------------------------ */

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'
const NS_MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main'
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
const NS_PKG_REL = 'http://schemas.openxmlformats.org/package/2006/relationships'

function contentTypes(sheetCount: number): string {
  const sheets = Array.from(
    { length: sheetCount },
    (_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
  ).join('')

  return (
    XML_HEAD +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    sheets +
    '</Types>'
  )
}

const ROOT_RELS =
  XML_HEAD +
  `<Relationships xmlns="${NS_PKG_REL}">` +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '</Relationships>'

function workbook(names: string[]): string {
  const sheets = names
    .map((n, i) => `<sheet name="${escapeXml(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('')

  return (
    XML_HEAD +
    `<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
    /* ⚠️ کتابِ کار هم راست‌به‌چپ — وگرنه نوارِ برگه‌ها از چپ شروع می‌شود. */
    '<bookViews><workbookView rightToLeft="1"/></bookViews>' +
    `<sheets>${sheets}</sheets>` +
    '</workbook>'
  )
}

function workbookRels(sheetCount: number): string {
  const sheets = Array.from(
    { length: sheetCount },
    (_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
  ).join('')

  return (
    XML_HEAD +
    `<Relationships xmlns="${NS_PKG_REL}">` +
    sheets +
    `<Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    '</Relationships>'
  )
}

/*
 | ⚠️ `[h]:mm` و نه `h:mm`: جمعِ کارکردِ ماه از ۲۴ ساعت می‌گذرد و `h:mm`
 | آن را به «روز» می‌پیچاند — ۱۶۰ ساعت «۱۶:۰۰» دیده می‌شد.
 */
const STYLES =
  XML_HEAD +
  `<styleSheet xmlns="${NS_MAIN}">` +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="[h]:mm"/></numFmts>' +
  '<fonts count="2">' +
  '<font><sz val="11"/><name val="Tahoma"/></font>' +
  '<font><b/><sz val="11"/><name val="Tahoma"/></font>' +
  '</fonts>' +
  '<fills count="3">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '<fill><patternFill patternType="solid"><fgColor rgb="FFEEF1F5"/><bgColor indexed="64"/></patternFill></fill>' +
  '</fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="3">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '</cellXfs>' +
  '</styleSheet>'

function worksheet(sheet: XlsxSheet): string {
  const colCount = Math.max(sheet.header.length, ...sheet.rows.map((r) => r.length))

  const cols = sheet.widths?.length
    ? '<cols>' +
      sheet.widths
        .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
        .join('') +
      '</cols>'
    : ''

  const headerRow =
    '<row r="1">' +
    sheet.header.map((h, c) => cellXml(h, c, 1, STYLE_HEADER)).join('') +
    '</row>'

  const bodyRows = sheet.rows
    .map((row, i) => {
      const r = i + 2

      return `<row r="${r}">${row.map((v, c) => cellXml(v, c, r)).join('')}</row>`
    })
    .join('')

  const lastRef = `${columnLetter(Math.max(colCount, 1) - 1)}${sheet.rows.length + 1}`

  return (
    XML_HEAD +
    `<worksheet xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
    `<dimension ref="A1:${lastRef}"/>` +
    /* راست‌به‌چپ و سرستونِ ثابت هنگامِ پیمایش. */
    '<sheetViews><sheetView workbookViewId="0" rightToLeft="1">' +
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
    '</sheetView></sheetViews>' +
    cols +
    `<sheetData>${headerRow}${bodyRows}</sheetData>` +
    '</worksheet>'
  )
}

function cellXml(value: XlsxCell, col: number, row: number, style = 0): string {
  const ref = `${columnLetter(col)}${row}`
  const s = style ? ` s="${style}"` : ''

  if (value === null || value === undefined || value === '') return ''

  if (typeof value === 'number') {
    return Number.isFinite(value) ? `<c r="${ref}"${s}><v>${value}</v></c>` : ''
  }

  if (typeof value === 'object') {
    /* مدت در اکسل کسری از روز است. */
    return `<c r="${ref}" s="${STYLE_DURATION}"><v>${value.minutes / 1440}</v></c>`
  }

  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`
}

function columnLetter(index: number): string {
  let n = index + 1
  let out = ''

  while (n > 0) {
    const rem = (n - 1) % 26
    out = String.fromCharCode(65 + rem) + out
    n = Math.floor((n - 1) / 26)
  }

  return out
}

/**
 * ⚠️ نویسه‌های کنترلیِ غیرمجاز در XML هم حذف می‌شوند — یک «خروجی کلیدی»
 * که از جایی کپی شده و `\u000B` دارد، کلِ فایل را خراب‌شده نشان می‌داد.
 */
function escapeXml(value: string): string {
  return value
    // oxlint-disable-next-line no-control-regex -- حذفِ همین نویسه‌ها هدف است
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** نام برگه: حداکثر ۳۱ نویسه، بدون `[]:*?/\` و یکتا. */
function uniqueSheetNames(names: string[]): string[] {
  const seen = new Set<string>()

  return names.map((raw, i) => {
    const base = raw.replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31) || `Sheet${i + 1}`
    let name = base
    let n = 2

    while (seen.has(name.toLowerCase())) {
      const suffix = ` (${n++})`
      name = base.slice(0, 31 - suffix.length) + suffix
    }

    seen.add(name.toLowerCase())

    return name
  })
}

/* ------------------------------------------------------------------
   ZIP بدون فشرده‌سازی (روش «stored»)
   ------------------------------------------------------------------ */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)

  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }

  return table
})()

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8)

  return (crc ^ 0xffffffff) >>> 0
}

function zip(files: Array<[string, string]>): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder()
  const locals: Uint8Array[] = []
  const centrals: Uint8Array[] = []
  let offset = 0

  /* ۱۹۸۰/۰۱/۰۱ — تاریخِ درونِ ZIP برای اکسل بی‌اهمیت است. */
  const DOS_TIME = 0
  const DOS_DATE = (0 << 9) | (1 << 5) | 1

  for (const [path, content] of files) {
    const name = encoder.encode(path)
    const data = encoder.encode(content)
    const crc = crc32(data)

    const local = new Uint8Array(30 + name.length + data.length)
    const lv = new DataView(local.buffer)
    lv.setUint32(0, 0x04034b50, true)
    lv.setUint16(4, 20, true)
    lv.setUint16(6, 0x0800, true) // نام‌ها UTF-8
    lv.setUint16(8, 0, true) // stored
    lv.setUint16(10, DOS_TIME, true)
    lv.setUint16(12, DOS_DATE, true)
    lv.setUint32(14, crc, true)
    lv.setUint32(18, data.length, true)
    lv.setUint32(22, data.length, true)
    lv.setUint16(26, name.length, true)
    lv.setUint16(28, 0, true)
    local.set(name, 30)
    local.set(data, 30 + name.length)

    const central = new Uint8Array(46 + name.length)
    const cv = new DataView(central.buffer)
    cv.setUint32(0, 0x02014b50, true)
    cv.setUint16(4, 20, true)
    cv.setUint16(6, 20, true)
    cv.setUint16(8, 0x0800, true)
    cv.setUint16(10, 0, true)
    cv.setUint16(12, DOS_TIME, true)
    cv.setUint16(14, DOS_DATE, true)
    cv.setUint32(16, crc, true)
    cv.setUint32(20, data.length, true)
    cv.setUint32(24, data.length, true)
    cv.setUint16(28, name.length, true)
    cv.setUint32(42, offset, true)
    central.set(name, 46)

    locals.push(local)
    centrals.push(central)
    offset += local.length
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0)
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, files.length, true)
  ev.setUint16(10, files.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true)

  const out = new Uint8Array(offset + centralSize + end.length)
  let pos = 0
  for (const part of [...locals, ...centrals, end]) {
    out.set(part, pos)
    pos += part.length
  }

  return out
}
